import { Box } from '@mui/material';
import { useMemo } from 'react';

interface MarkdownContentProps {
  content: string;
  isUser?: boolean;
}

/**
 * Simple markdown renderer for chat messages
 * Supports: code blocks, inline code, bold, italic, lists, links
 */
export default function MarkdownContent({ content, isUser = false }: MarkdownContentProps) {
  const formattedContent = useMemo(() => {
    let html = content;

    // Code blocks with language (```language\ncode\n```)
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
      const language = lang || 'text';
      return `<pre class="code-block"><code class="language-${language}">${escapeHtml(code.trim())}</code></pre>`;
    });

    // Inline code (`code`)
    html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

    // Bold (**text** or __text__)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic (*text* or _text_)
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Unordered lists (- item or * item)
    html = html.replace(/^[\-\*]\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

    // Ordered lists (1. item)
    html = html.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => {
      if (!match.includes('<ul>')) {
        return `<ol>${match}</ol>`;
      }
      return match;
    });

    // Headers (# H1, ## H2, etc.)
    html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

    // Line breaks
    html = html.replace(/\n/g, '<br />');

    return html;
  }, [content]);

  return (
    <Box
      sx={{
        '& pre.code-block': {
          bgcolor: isUser ? 'rgba(0, 0, 0, 0.2)' : 'grey.100',
          p: 2,
          borderRadius: 1,
          overflowX: 'auto',
          my: 1,
        },
        '& code.inline-code': {
          bgcolor: isUser ? 'rgba(0, 0, 0, 0.2)' : 'grey.200',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& a': {
          color: isUser ? 'inherit' : 'primary.main',
          textDecoration: 'underline',
        },
        '& ul, & ol': {
          pl: 3,
          my: 1,
        },
        '& li': {
          mb: 0.5,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          mt: 2,
          mb: 1,
          fontWeight: 600,
        },
        '& h1': { fontSize: '1.75rem' },
        '& h2': { fontSize: '1.5rem' },
        '& h3': { fontSize: '1.25rem' },
        '& h4': { fontSize: '1.1rem' },
        '& h5, & h6': { fontSize: '1rem' },
        wordBreak: 'break-word',
      }}
      dangerouslySetInnerHTML={{ __html: formattedContent }}
    />
  );
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}
